import { API_BASE_URL, APIError } from './idp-api.js';

export type { APIError };

type ApiErrorPayload = {
  error?: {
    code?: string;
    message?: string;
  };
};

async function fetchAdminJSON<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const { headers, ...rest } = options;
  const response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...rest,
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    credentials: 'include',
  });

  if (!response.ok) {
    const payload = (await response.json().catch(() => null)) as ApiErrorPayload | null;
    throw new APIError(
      response.status,
      payload?.error?.code ?? 'UNKNOWN_ERROR',
      payload?.error?.message ?? 'An unknown error occurred'
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return response.json() as Promise<T>;
}

export type IdentitySummary = {
  identityId: string;
  email: string;
  name: string;
  emailVerified: boolean;
  createdAt: string;
};

export type IdentityDetail = IdentitySummary & {
  groups: Array<{ groupId: string; name: string; joinedAt: string }>;
};

export type GroupSummary = {
  groupId: string;
  name: string;
  isSystem: boolean;
  memberCount: number;
  createdAt: string;
  updatedAt: string;
};

export type MemberSummary = {
  identityId: string;
  email: string;
  name: string;
  joinedAt: string;
};

export type GroupDetail = Omit<GroupSummary, 'memberCount'> & {
  members: MemberSummary[];
};

export type Pagination = {
  limit: number;
  hasMore: boolean;
  nextCursor: string | null;
};

type EmptyResponse = Record<string, never>;

export const adminApi = {
  listIdentities: (cursor?: string, limit?: number) => {
    const params = new URLSearchParams();
    if (cursor !== undefined && cursor !== '') params.set('cursor', cursor);
    if (limit !== undefined) params.set('limit', String(limit));
    const qs = params.toString();
    return fetchAdminJSON<{ data: IdentitySummary[]; pagination: Pagination }>(
      `/admin/identities${qs !== '' ? `?${qs}` : ''}`
    );
  },

  getIdentity: (identityId: string) =>
    fetchAdminJSON<IdentityDetail>(`/admin/identities/${encodeURIComponent(identityId)}`),

  listGroups: () => fetchAdminJSON<{ data: GroupSummary[] }>('/admin/groups'),

  getGroup: (groupId: string) =>
    fetchAdminJSON<GroupDetail>(`/admin/groups/${encodeURIComponent(groupId)}`),

  addMember: (groupId: string, identityId: string) =>
    fetchAdminJSON<{ groupId: string; identityId: string; createdAt: string }>(
      `/admin/groups/${encodeURIComponent(groupId)}/members`,
      {
        method: 'POST',
        body: JSON.stringify({ identityId }),
      }
    ),

  removeMember: (groupId: string, identityId: string) =>
    fetchAdminJSON<EmptyResponse>(
      `/admin/groups/${encodeURIComponent(groupId)}/members/${encodeURIComponent(identityId)}`,
      { method: 'DELETE' }
    ),
};
